import React, { useState } from 'react';
import { Link, useNavigate  } from 'react-router-dom'; // 确保导入Link组件
import './Register.css';

const RegisterForm = () => {
	// Form state with checkboxes defaulted to checked
	const [formData, setFormData] = useState({
		fullName: '',
		email: '',
		password: '',
		confirmPassword: '',
		agreeTerms: true,  // Default to checked
		agreeEmails: true  // Default to checked
	});

	const navigate = useNavigate();
	// Error state
	const [errors, setErrors] = useState({});

	// Submission state
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);

	// Handle input changes
	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData({
			...formData,
			[name]: type === 'checkbox' ? checked : value
		});

		// Clear corresponding field error
		if (errors[name]) {
			setErrors({
				...errors,
				[name]: ''
			});
		}
	};

	// Validate form
	const validateForm = () => {
		const newErrors = {};

		// Validate full name
		if (!formData.fullName) {
			newErrors.fullName = 'Please enter your name';
		} else if (formData.fullName.length < 2) {
			newErrors.fullName = 'Name must be at least 2 characters';
		}

		// Validate email
		if (!formData.email) {
			newErrors.email = 'Please enter your email';
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = 'Please enter a valid email address';
		}

		// Validate password
		if (!formData.password) {
			newErrors.password = 'Please enter a password';
		} else if (formData.password.length < 8) {
			newErrors.password = 'Password must be at least 8 characters';
		} else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
			newErrors.password = 'Password must include uppercase, lowercase letters and numbers';
		}

		// Validate confirm password
		if (!formData.confirmPassword) {
			newErrors.confirmPassword = 'Please confirm your password';
		} else if (formData.confirmPassword !== formData.password) {
			newErrors.confirmPassword = 'Passwords do not match';
		}

		// Validate terms agreement
		if (!formData.agreeTerms) {
			newErrors.agreeTerms = 'You must agree to the Terms of Use';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Submit form
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate form
		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			// Replace with actual API call
			// Example: const response = await axios.post('/api/register', formData);

			// Simulate API call
			await new Promise(resolve => setTimeout(resolve, 1000));

			// Registration successful
			setSubmitSuccess(true);

			// Reset form
			setFormData({
				fullName: '',
				email: '',
				password: '',
				confirmPassword: '',
				agreeTerms: true,  // Reset to default checked
				agreeEmails: true   // Reset to default checked
			});

		} catch (error) {
			console.error('Registration failed:', error);
			setErrors({ submit: 'Registration failed, please try again later' });
		} finally {
			setIsSubmitting(false);
		}
	};

	// Reset form for new registration


	const handleGoHome = () => {
		navigate('/');
	};

	// If registration is successful, show success message
	if (submitSuccess) {
		return (
			<div className="register-success">
				<h2>Registration Successful!</h2>
				<p>Your account has been created. You can now log in to access our services.</p>
				<button onClick={handleGoHome}>Return to Home Page</button>
			</div>
		);
	}

	return (
		<div className="register-container">
			<h2>User Registration</h2>
			<p>Create an account to use all our services</p>

			<form onSubmit={handleSubmit} className="register-form">
				<div className="form-group">
					<label htmlFor="fullName">Full Name *</label>
					<input
						type="text"
						id="fullName"
						name="fullName"
						value={formData.fullName}
						onChange={handleChange}
						placeholder="Enter your full name"
						className={errors.fullName ? 'error' : ''}
					/>
					{errors.fullName && <p className="error-message">{errors.fullName}</p>}
				</div>

				<div className="form-group">
					<label htmlFor="email">Email Address *</label>
					<input
						type="email"
						id="email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						placeholder="Enter your email address"
						className={errors.email ? 'error' : ''}
					/>
					{errors.email && <p className="error-message">{errors.email}</p>}
				</div>

				<div className="form-group">
					<label htmlFor="password">Password *</label>
					<input
						type="password"
						id="password"
						name="password"
						value={formData.password}
						onChange={handleChange}
						placeholder="Create a password (min. 8 characters)"
						className={errors.password ? 'error' : ''}
					/>
					{errors.password && <p className="error-message">{errors.password}</p>}
				</div>

				<div className="form-group">
					<label htmlFor="confirmPassword">Confirm Password *</label>
					<input
						type="password"
						id="confirmPassword"
						name="confirmPassword"
						value={formData.confirmPassword}
						onChange={handleChange}
						placeholder="Confirm your password"
						className={errors.confirmPassword ? 'error' : ''}
					/>
					{errors.confirmPassword && <p className="error-message">{errors.confirmPassword}</p>}
				</div>

				<div className="form-group checkbox-group">
					<div className="checkbox-container">
						<input
							type="checkbox"
							id="agreeTerms"
							name="agreeTerms"
							checked={formData.agreeTerms}
							onChange={handleChange}
							className={errors.agreeTerms ? 'error' : ''}
						/>
						<label htmlFor="agreeTerms">
							I agree to the <Link to="/tou">Terms of Use</Link> and <Link to="/privacy">Privacy Policy</Link> *
						</label>
					</div>
					{errors.agreeTerms && <p className="error-message">{errors.agreeTerms}</p>}
				</div>

				<div className="form-group checkbox-group">
					<div className="checkbox-container">
						<input
							type="checkbox"
							id="agreeEmails"
							name="agreeEmails"
							checked={formData.agreeEmails}
							onChange={handleChange}
						/>
						<label htmlFor="agreeEmails">I agree to receive notification emails and updates</label>
					</div>
				</div>

				{errors.submit && <p className="error-message submit-error">{errors.submit}</p>}

				<button
					type="submit"
					className="submit-button"
					disabled={isSubmitting}
				>
					{isSubmitting ? 'Registering...' : 'Register'}
				</button>

				{/*<p className="login-link">Already have an account? <Link to="/login">Log in</Link></p>*/}
			</form>
		</div>
	);
};

export default RegisterForm;